/** @jsxImportSource theme-ui */
import { Flex, get, Theme, Label, Grid, Input, Button, Box, Spinner, Text } from 'theme-ui';
import { XIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid';
import { useCombobox } from 'downshift';
import React, { useEffect, useRef } from 'react';

interface Item {
    value: string | number;
    label: string;
}
interface Props {
    label: string;
    placeholder?: string;
    items: Item[];
    onSearchChange?: (text: string) => void;
    onSelect?: (item: Item) => void;
    isLoading?: boolean;
    total?: number;
    value?: string;
}

const filterCompanies = (item, inputValue) =>
    item.label.toLowerCase().includes(inputValue.toLowerCase());

const valueMapper = (inputItem) => inputItem.value;

const DropdownComboBox: React.FC<Props> = ({
    label,
    placeholder,
    items = [],
    total,
    isLoading = false,
    onSearchChange = (text: string) => {},
    onSelect = (item) => {},
    ...props
}) => {
    const [inputItems, setInputItems] = React.useState(items);

    const {
        isOpen,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        getInputProps,
        highlightedIndex,
        getItemProps,
        selectedItem,
        selectItem,
        reset,
    } = useCombobox({
        items: inputItems,
        itemToString: (item) => (item ? item.label : ''),
        onInputValueChange: ({ inputValue }) => {
            onSearchChange(inputValue);
            setInputItems(items.filter((item) => filterCompanies(item, inputValue)));
        },
    });

    const inputValue = getInputProps().value;

    useEffect(() => {
        if (inputItems.map(valueMapper).sort().join() !== items.map(valueMapper).sort().join()) {
            const itemsMatchingSearch = items.filter((item) => filterCompanies(item, inputValue));

            const newItems = items.filter((item) => {
                if (itemsMatchingSearch.map((i) => i.value).includes(item.value)) {
                    return false;
                }

                return true;
            });

            setInputItems([...itemsMatchingSearch, ...newItems]);
        }
    }, [items, inputValue]);

    const mounted = useRef(false);
    useEffect(() => {
        if (mounted.current) onSelect(selectedItem);
        mounted.current = true;
    }, [selectedItem]);

    useEffect(() => {
        if (!props.value) {
            reset();
        }
    }, [props.value]);

    return (
        <Grid sx={{ position: 'relative', gridRow: '1fr', gridColumn: '1fr', gap: 0 }}>
            <Label {...getLabelProps()}>{label}</Label>
            <Flex>
                <Input
                    p="3"
                    m="0"
                    backgroundColor="gray.1"
                    {...getInputProps()}
                    data-testid="combobox-input"
                    placeholder={placeholder}
                />
                <Button
                    backgroundColor="gray.1"
                    variant="insidePlain"
                    style={{ padding: '4px 8px' }}
                    aria-label="toggle menu"
                    data-testid="combobox-toggle-button"
                    {...getToggleButtonProps()}
                >
                    {isOpen ? (
                        <ChevronUpIcon width={16} height={16} />
                    ) : (
                        <ChevronDownIcon width={16} height={16} />
                    )}
                </Button>
                <Button
                    backgroundColor="gray.1"
                    variant="insidePlain"
                    style={{ padding: '4px 8px' }}
                    aria-label="toggle menu"
                    data-testid="clear-button"
                    onClick={() => selectItem(null)}
                >
                    <XIcon width={16} height={16} />
                </Button>
            </Flex>
            <Box {...getMenuProps()}>
                {isOpen ? (
                    <Box
                        backgroundColor="white"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: 100,
                            width: '100%',
                            gridRow: 3,
                            gridColumn: 1,
                            borderBottomLeftRadius: 'sm',
                            borderBottomRightRadius: 'sm',
                            boxShadow: (t: Theme) => get(t, 'shadows.menu'),
                        }}
                    >
                        <ul
                            sx={{
                                listStyle: 'none',
                                padding: 2,
                                margin: 0,
                            }}
                        >
                            {isOpen &&
                                inputItems.map((item, index) => (
                                    <li
                                        sx={{
                                            p: 1,
                                            borderRadius: 'sm',
                                            backgroundColor:
                                                highlightedIndex === index ? 'blue.1' : null,
                                        }}
                                        key={`${item.value}${index}`}
                                        {...getItemProps({
                                            key: item.value,
                                            item,
                                            index,
                                        })}
                                    >
                                        {item.label}
                                    </li>
                                ))}
                            {isLoading ? (
                                <Flex sx={{ justifyContent: 'center' }}>
                                    <Spinner size="16" />
                                </Flex>
                            ) : null}
                            {total && items.length < total ? (
                                <Box>
                                    <Text color="muted">
                                        Not everything is listed, type in the letters to load more.
                                        Try adding a space after a part of word too.
                                    </Text>
                                </Box>
                            ) : null}
                        </ul>
                    </Box>
                ) : null}
            </Box>
        </Grid>
    );
};

export default DropdownComboBox;
